/**
 * navIcon.ts — resolves a backend-supplied nav icon name to a Material Icons
 * ligature, and groups top-level nav items into sections.
 *
 * WHY THIS EXISTS: the JWT menu payload mixes icon vocabularies. Most rows use
 * Material names (`local_shipping`, `route`, `folder`, `iconType:
 * "material-icons"`), but some use Font Awesome (`fa-house`, `iconType:
 * "fa-solid"`). The app renders every one of them through MUI's <Icon>, which
 * looks up a *Material* ligature by name — so a Font Awesome name matches
 * nothing and the row renders an empty icon gutter. That is what "Home" was
 * doing: a blank slot next to three siblings that had icons.
 *
 * Fixing it in the frontend rather than the menu table keeps a data change off
 * the critical path, and survives new rows arriving with either vocabulary.
 *
 * Kept as a .ts module (not alongside the component) so AppNavList.tsx exports
 * only components — react-refresh/only-export-components fails the lint gate
 * otherwise.
 */

/**
 * Font Awesome → Material Icons. Only the names the menu actually ships plus
 * near neighbours worth pre-empting; anything unmapped falls through to the
 * generic resolution below rather than rendering blank.
 */
const FA_TO_MATERIAL: Record<string, string> = {
  house: 'home',
  home: 'home',
  'truck-pickup': 'local_shipping',
  truck: 'local_shipping',
  'truck-fast': 'local_shipping',
  route: 'route',
  'map-location-dot': 'map',
  map: 'map',
  folder: 'folder',
  'folder-open': 'folder_open',
  user: 'person',
  users: 'group',
  'user-group': 'group',
  gear: 'settings',
  cog: 'settings',
  'file-lines': 'description',
  file: 'description',
  'chart-line': 'show_chart',
  'chart-simple': 'bar_chart',
  bell: 'notifications',
  'clipboard-list': 'assignment',
  wrench: 'build',
  'circle-question': 'help',
};

/** Rendered when a name resolves to nothing — never leave the gutter empty. */
const FALLBACK_ICON = 'chevron_right';

/**
 * Turn whatever the menu row carries into a Material ligature.
 *
 * Font Awesome names are stripped of their `fa-` prefix and mapped. Material
 * names pass through untouched. Anything unrecognised gets the fallback, so a
 * new icon vocabulary degrades to a visible glyph instead of a blank.
 */
export function resolveNavIcon(icon?: string | null): string {
  const raw = (icon ?? '').trim();
  if (!raw) return FALLBACK_ICON;

  // `fa-house`, `fa-solid fa-house`, `fas fa-house` → `house`
  if (raw.includes('fa-')) {
    const name = raw
      .split(/\s+/)
      .filter((part) => part.startsWith('fa-'))
      .map((part) => part.slice(3))
      // `fa-solid` / `fa-regular` are style tokens, not icon names.
      .filter((part) => !['solid', 'regular', 'brands', 'light', 'thin'].includes(part))
      .pop();
    return (name && FA_TO_MATERIAL[name]) ?? FALLBACK_ICON;
  }

  // Already a Material ligature. Normalise spaces to underscores — the font
  // matches `local_shipping`, not `local shipping`.
  return raw.replace(/\s+/g, '_');
}

// ── Sections ────────────────────────────────────────────────────────────────

export type NavSection = 'operations' | 'setup';

/**
 * Routes that are configuration rather than day-to-day work. Everything else
 * is treated as operations, so a menu row added tomorrow lands in the working
 * section by default instead of disappearing under a "Setup" heading.
 */
const SETUP_ROUTES = new Set([
  'catalogs',
  'configuration',
  'settings',
  'admin',
  'users',
  'roles',
  'permissions',
]);

/** i18n key for a section eyebrow. */
export const SECTION_LABEL_KEY: Record<NavSection, string> = {
  operations: 'nav.section.operations',
  setup: 'nav.section.setup',
};

/**
 * Menu routes that actually resolve to the dashboard at "/".
 *
 * The backend hardcodes Home's route as "/home" (LoginBLL.GetMenuV2) and
 * routes.tsx redirects "/home" → "/". Because of that redirect the URL never
 * equals the menu route, so a plain `pathname === path` comparison left NO row
 * selected on the screen every user lands on after signing in — the rail read
 * as having no current page at all.
 */
const DASHBOARD_ROUTES = new Set(['home', 'dashboard']);

export function isDashboardRoute(route: string): boolean {
  const slug = route.replace(/^\/+|\/+$/g, '').split('/')[0].toLowerCase();
  return DASHBOARD_ROUTES.has(slug);
}

/**
 * Full routes that are operations despite living under a setup-looking prefix.
 *
 * Chat is the case this exists for: the backend hardcodes its path as
 * `catalogs/administrator-chat`, so the first-slug rule below files an inbox
 * under Setup. Matched on the whole route, not the prefix, so it cannot
 * accidentally pull the rest of `catalogs` across with it.
 */
const OPERATIONS_ROUTES = new Set(['catalogs/administrator-chat']);

export function sectionForRoute(route: string): NavSection {
  const full = route.replace(/^\/+|\/+$/g, '').toLowerCase();
  if (OPERATIONS_ROUTES.has(full)) return 'operations';
  const slug = full.split('/')[0];
  return SETUP_ROUTES.has(slug) ? 'setup' : 'operations';
}
